import { Control, Controller, useWatch } from 'react-hook-form';
import { DataRoomFormData } from '../formData';
import { Column, FixedColumn, Row } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import RadioButtons from 'further-ui/components/RadioButtons';
import LogoUploadBox from 'components/new/LogoUploadBox';
import { TextField, Typography } from '@mui/material';
import EditCta from './EditCta';

import PageSection from 'components/new/PageSection';
import TypographyButton from 'further-ui/components/TypographyButton';
import KeyValueList from 'components/new/KeyValueList';

type Props = {
  control: Control<DataRoomFormData>;
  defaultLogoUri: string;
  panelColor: string | undefined;
};

const Content: React.FC<Props> = ({ control, defaultLogoUri, panelColor }) => {
  const useCustomLogo = useWatch({
    control,
    name: 'logoSettings.useCustomLogo',
  });

  return (
    <FixedColumn fixedWidth="xl" spacing="lg">
      <PageSection spacing="lg">
        <EditCta />
        <FormField spacing="sm" label="Data room logo">
          <Column spacing="xs">
            <Controller
              name="logoSettings.useCustomLogo"
              control={control}
              render={({ field }) => (
                <RadioButtons
                  value={field.value ? 'custom' : 'default'}
                  onChange={(value) => field.onChange(value === 'custom')}
                  direction="row"
                  options={[
                    { value: 'default', label: 'Default portal logo' },
                    { value: 'custom', label: 'Upload custom logo' },
                  ]}
                />
              )}
            />
            <Column spacing="sm">
              {useCustomLogo ? null : (
                <Typography variant="body2">
                  Used in the portal header of your data room, upload a logo
                  with a transparent background that appears clearly on your
                  primary background colour.
                </Typography>
              )}
              <Controller
                name="logoSettings.customLogo"
                control={control}
                render={({ field }) => (
                  <LogoUploadBox
                    value={
                      useCustomLogo
                        ? field.value ?? null
                        : { previewUri: defaultLogoUri, objectKey: '' }
                    }
                    disabled={!useCustomLogo}
                    onChange={field.onChange}
                    boxVariant="dark"
                    backgroundColor={panelColor}
                  />
                )}
              />
            </Column>
          </Column>
        </FormField>
      </PageSection>
      <PageSection
        spacing="lg"
        title={
          <Column spacing="xs">
            <Typography variant="h6">Investment opportunities page</Typography>
            <Typography variant="body2">
              Summary information displayed on the master{' '}
              <TypographyButton
                text="investment
              opportunities page"
              />
              .
            </Typography>
          </Column>
        }
      >
        <Row spacing="sm" breakpoint="sm">
          <FormField label="Title">
            <Controller
              name="investmentOpportunitiesContent.title"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Enter title"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField label="Subtitle">
            <Controller
              name="investmentOpportunitiesContent.subtitle"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Enter subtitle"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
        </Row>
        <FormField label="Description">
          <Controller
            name="investmentOpportunitiesContent.description"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                multiline
                rows={7}
                fullWidth
                size="small"
                placeholder="Enter description"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>
      </PageSection>
      <PageSection
        withoutBottomDivider
        spacing="lg"
        title={
          <Column spacing="xs">
            <Typography variant="h6">Custom metric</Typography>
            <Typography variant="body2">
              Custom metric(s) displayed on the master{' '}
              <TypographyButton
                text="investment
              opportunities page"
              />
              .
            </Typography>
          </Column>
        }
      >
        <Controller
          name="investmentOpportunitiesContent.metrics"
          control={control}
          render={({ field }) => (
            <KeyValueList
              value={field.value ?? []}
              onChange={(items) => {
                field.onChange(items);
              }}
              fieldNamePlaceholder="Enter metric name"
              valuePlaceholder="Enter metric value"
            />
          )}
        />
      </PageSection>
    </FixedColumn>
  );
};

export default Content;
